import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useParams, useSearchParams } from "@remix-run/react";
import { COURSE_ACCESS_LEVEL, SUPPORTED_LANGUAGES, isCourseFree } from "@repo/shared";
import { AxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ApiClient } from "~/api/api-client";
import { getApiErrorMessage } from "~/api/apiError";
import { useDirectEnroll } from "~/api/mutations/useDirectEnroll";
import { useCourse } from "~/api/queries";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import isotipoImg from "~/assets/academia-isotipo-256.webp";
import { AuthGuardButtonLabel, AuthGuardSlot, isAuthGuardLocked } from "~/components/AuthGuard";
import { BotTrap } from "~/components/BotTrap";
import { PhoneField, isValidPhoneNumber } from "~/components/PhoneField";
import { TermsCheckbox } from "~/components/TermsCheckbox";
import { OtpVerificationCard } from "~/components/ui/OtpVerificationCard";
import { useToast } from "~/components/ui/use-toast";
import { useAuthGuard } from "~/hooks/useAuthGuard";
import {
  trackFreeEnrollment,
  trackLead,
  trackOtpRequested,
  trackSignUp,
} from "~/lib/tracking/events";
import { cn } from "~/lib/utils";
import { CATALOG_PATH } from "~/utils/catalogPath";
import { setPageTitle } from "~/utils/setPageTitle";

import type { MetaFunction } from "@remix-run/react";
import type { ReactNode } from "react";
import type { CurrentUserResponse } from "~/api/generated-api";

export const meta: MetaFunction = ({ matches }) => setPageTitle(matches, "pages.register");

type Step = "form" | "otp";

/**
 * Los rechazos de la API ya vienen en castellano; los `Error` propios de esta página también
 * (p. ej. "No pudimos validar tu cuenta"). Lo que no hay que mostrar nunca es el texto técnico de
 * axios ("Network Error", "Request failed with status code 500").
 */
const errMsg = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) return getApiErrorMessage(error, fallback);
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

/**
 * Module-scope on purpose: a component defined INSIDE CourseEnrollPage would be
 * a brand-new function reference every render, so React would unmount/remount
 * this subtree (and the form inputs) on every keystroke, dropping input focus.
 */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-md px-4 py-12">
      <div className="rounded-[12px] border border-landing-border bg-landing-white p-8 shadow-landing-md sm:p-10">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <img
            src={isotipoImg}
            alt=""
            width={44}
            height={44}
            className="h-10 w-10 object-contain"
          />
          <span className="font-display text-[26px] font-semibold leading-none tracking-tight">
            <span className="text-landing-text">Academ</span>
            <span className="text-landing-red">IA</span>
          </span>
        </Link>
        {children}
      </div>
    </div>
  );
}

export default function CourseEnrollPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { slug = "" } = useParams();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: course, isLoading } = useCourse(slug, SUPPORTED_LANGUAGES.ES);
  const { data: currentUser, isPending: isSessionPending } = useCurrentUser();
  const { mutateAsync: directEnroll, isPending: isConfirming } = useDirectEnroll();

  // `?nivel=preview` pide la muestra de un curso PAGO. Sin el parámetro se asume que se
  // quiere el acceso completo, que es lo único que tiene sentido en un curso gratis.
  const wantsPreview = searchParams.get("nivel") === "preview";
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("form");
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Submit intentado sin términos: validación visible en vez de botón muerto.
  const [termsError, setTermsError] = useState(false);
  // Sube en cada intento fallido para que el aviso vuelva a salir aunque `termsError`
  // ya estuviera en true (ver `errorNonce` en TermsCheckbox).
  const [termsErrorNonce, setTermsErrorNonce] = useState(0);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const guard = useAuthGuard("enroll");
  const { send: sendGuarded, phoneChanged } = guard;

  const enrollFormSchema = useMemo(
    () =>
      z.object({
        phone: z.string().refine((value) => !!value && isValidPhoneNumber(value), {
          message: t("registerView.validation.phone"),
        }),
        firstName: z.string().min(1, { message: t("registerView.validation.firstName") }),
        lastName: z.string().min(1, { message: t("registerView.validation.lastName") }),
      }),
    [t],
  );

  type EnrollFormData = z.infer<typeof enrollFormSchema>;

  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: { errors, isValid },
  } = useForm<EnrollFormData>({
    resolver: zodResolver(enrollFormSchema),
    mode: "onChange",
    // El teléfono arranca VACÍO, no en "+54". El campo dibuja el prefijo del país aparte
    // (por CSS, al lado de la banderita) y en el input va sólo el número nacional: sembrarlo
    // con "+54" hacía que el prefijo se viera dos veces, y el segundo se podía borrar.
    defaultValues: { phone: "", firstName: "", lastName: "" },
  });

  const [otpError, setOtpError] = useState<string | null>(null);

  // Destino único de la inscripción directa, para todos los caminos. Antes se entraba
  // directo a la primera lección, que dejaba a la persona adentro del reproductor sin
  // haber visto nunca de qué se trataba el curso ni qué acababa de obtener.
  const courseUrl = course ? `/course/${course.slug ?? course.id}/details` : "/courses";

  /** Pide el código. También es el "Reenviar código" del paso siguiente (true = salió). */
  const handleSendOTP = useCallback(async () => {
    // Bajar el teclado en mobile: abierto, tapa el paso del código o la validación.
    (document.activeElement as HTMLElement | null)?.blur?.();
    if (!termsAccepted) {
      setTermsError(true);
      setTermsErrorNonce((n) => n + 1);
      return false;
    }
    if (!getValues("phone")) return false;
    // Esperar la comprobación anti-bot en vez de mandar lo que haya en este instante: el widget
    // tarda alrededor de un segundo, y quien completa rápido llegaba al botón antes que el token.
    // Los rechazos del antibot se explican en el formulario, con cuenta regresiva (un toast rojo
    // de 4 segundos desaparecía antes de que la persona terminara de leerlo).
    const result = await sendGuarded(async (antibot) => {
      const sent = await ApiClient.instance.post<{ data?: { leadEventId?: string } }>(
        "/api/auth/send-otp",
        {
          phone: getValues("phone"),
          context: "register",
          source: "register",
          termsAccepted,
          // Nombre y apellido viajan con el pedido del código: quedan en el intento de registro
          // aunque la persona nunca valide el código (antes llegaban recién al crear la cuenta).
          firstName: getValues("firstName"),
          lastName: getValues("lastName"),
          ...antibot,
        },
      );
      return sent.data;
    });
    if (result.ok) {
      trackOtpRequested();
      trackLead(result.data?.data?.leadEventId);
      setStep("otp");
      return true;
    }
    if (result.reason === "error") {
      toast({
        variant: "destructive",
        description: errMsg(result.error, "No pudimos enviarte el código. Probá de nuevo."),
      });
    }
    return false;
  }, [getValues, sendGuarded, termsAccepted, toast]);

  const handleVerifyAndEnroll = useCallback(
    async (code: string) => {
      if (!course) return;
      const { phone, firstName, lastName } = getValues();
      setIsEnrolling(true);
      setOtpError(null);
      try {
        // 1. Verify the code (pilot bypass code is accepted by the API).
        const verifyRes = await ApiClient.instance.post("/api/auth/verify-otp", { phone, code });
        const result = verifyRes.data?.data as
          | (CurrentUserResponse["data"] & { isNewUser?: boolean; otpToken?: string })
          | undefined;

        // 2. New user → create the account. Existing user → already logged in by verify-otp.
        let authedUser = result as CurrentUserResponse["data"] | undefined;
        if (result?.isNewUser && result?.otpToken) {
          const regRes = await ApiClient.instance.post("/api/auth/register", {
            phone,
            firstName,
            lastName,
            otpToken: result.otpToken,
          });
          authedUser = (regRes.data?.data ?? regRes.data) as CurrentUserResponse["data"];
          trackSignUp(authedUser?.id);
        }

        if (!authedUser?.id) {
          throw new Error("No pudimos validar tu cuenta. Intentá de nuevo.");
        }

        // 3. Enroll in the free course AS PURCHASED. The plain enroll endpoint
        //    leaves purchasedAt NULL, and lesson access requires purchasedAt
        //    (or a freemium chapter), so it must go through the free-checkout
        //    path: add to the server cart, then POST /checkout/free (which
        //    enrolls with { purchased: true }). A 409 "Already purchased" just
        //    means the user already has access — treat it as success.
        try {
          await ApiClient.instance.post("/api/cart/items", { courseId: course.id });
        } catch {
          // Already in cart / cart noise — irrelevant, freeCheckout reads server cart.
        }
        try {
          const enrolled = await ApiClient.instance.post("/api/checkout/free", {
            courseIds: [course.id],
          });
          const order = enrolled.data?.data as
            | { orderId?: string; enrolledCourseIds?: string[]; upgradedCourseIds?: string[] }
            | undefined;
          // Queda en la cola de la sesión: se manda después de la navegación dura de abajo.
          trackFreeEnrollment(order?.orderId, [
            ...(order?.enrolledCourseIds ?? []),
            ...(order?.upgradedCourseIds ?? []),
          ]);
        } catch (enrollError) {
          const status =
            enrollError instanceof AxiosError ? enrollError.response?.status : undefined;
          if (status !== 409) throw enrollError; // 409 = already purchased = already has access
        }

        // Navegación DURA (no SPA): la pantalla de destino tiene que arrancar con la cookie
        // de sesión recién puesta. Con un navigate() del cliente se renderiza con el estado
        // de React Query previo a la autenticación y el curso aparece como no adquirido.
        window.location.assign(courseUrl);
      } catch (error) {
        // Inline, not a toast: this is feedback about the code they just typed.
        setOtpError(errMsg(error, "No pudimos completar la inscripción. Probá de nuevo."));
        setIsEnrolling(false);
      }
    },
    [course, courseUrl, getValues],
  );

  const inputClassName =
    "w-full rounded-[6px] border border-landing-border bg-landing-white px-3 py-2.5 text-[15px] text-landing-text outline-none transition-colors placeholder:text-landing-light focus:border-landing-red focus:ring-1 focus:ring-landing-red/30";

  if (isLoading) {
    return (
      <Shell>
        <p className="text-center text-[14px] text-landing-muted">Cargando…</p>
      </Shell>
    );
  }

  if (!course) {
    return (
      <Shell>
        <p className="text-center text-[15px] text-landing-text">No encontramos este curso.</p>
        <div className="mt-5 text-center">
          <Link to={CATALOG_PATH} className="font-medium text-landing-red underline">
            Ver todos los cursos
          </Link>
        </div>
      </Shell>
    );
  }

  // Qué se puede adquirir sin pagar: el curso entero si es gratis, o la muestra si es pago
  // y tiene capítulos abiertos. Pedir la muestra de un curso sin capítulos abiertos no es
  // un caso válido y se explica en vez de fallar silenciosamente.
  const courseIsFree = isCourseFree(course);
  const canPreview = Boolean(course.hasFreeChapter);
  const grantedLevel = courseIsFree ? COURSE_ACCESS_LEVEL.FULL : COURSE_ACCESS_LEVEL.FREE_PREVIEW;
  const canEnrollDirectly = courseIsFree || (wantsPreview && canPreview);

  if (!canEnrollDirectly) {
    return (
      <Shell>
        <p className="text-center text-[15px] text-landing-text">
          {wantsPreview
            ? "Este curso no tiene capítulos de muestra."
            : "Este curso no es gratuito. La inscripción directa es solo para cursos gratis."}
        </p>
        <div className="mt-5 text-center">
          <Link to={`/courses/${slug}`} className="font-medium text-landing-red underline">
            Ver el curso
          </Link>
        </div>
      </Shell>
    );
  }

  // A quien ya está identificado no se le puede mostrar un formulario de registro: antes
  // esta pantalla ignoraba la sesión, así que un alumno logueado que tocaba el link de la
  // home tenía que volver a pasar por teléfono y código como si fuera un desconocido.
  if (isSessionPending) {
    return (
      <Shell>
        <p className="text-center text-[14px] text-landing-muted">Cargando…</p>
      </Shell>
    );
  }

  if (currentUser) {
    const alreadyHasIt = Boolean(course.enrolled) && (courseIsFree ? course.purchased : true);

    const confirmEnroll = async () => {
      setConfirmError(null);
      try {
        await directEnroll({ courseId: course.id, accessLevel: grantedLevel });
        navigate(courseUrl);
      } catch (error) {
        setConfirmError(errMsg(error, "No pudimos completar la inscripción. Probá de nuevo."));
      }
    };

    return (
      <Shell>
        <div className="mb-6 text-center">
          <p className="text-[13px] font-medium uppercase tracking-wide text-landing-red">
            {grantedLevel === COURSE_ACCESS_LEVEL.FULL
              ? "Inscripción gratuita"
              : "Muestra gratuita"}
          </p>
          <h1 className="mt-2 font-display text-[22px] font-bold leading-tight tracking-tight text-landing-text">
            {course.title}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-landing-muted">
            {alreadyHasIt
              ? "Ya lo tenés en tu aula."
              : grantedLevel === COURSE_ACCESS_LEVEL.FULL
                ? `Hola ${currentUser.firstName}, confirmá y el curso queda en tu aula.`
                : `Hola ${currentUser.firstName}, confirmá para abrir los capítulos de muestra.`}
          </p>
        </div>

        {confirmError && (
          <p
            role="alert"
            className="mb-4 rounded-[8px] border border-landing-red/30 bg-landing-red-50 px-3 py-2 text-[13.5px] text-landing-red"
          >
            {confirmError}
          </p>
        )}

        {alreadyHasIt ? (
          <Link
            to={courseUrl}
            className="block w-full rounded-[8px] bg-landing-red px-6 py-3 text-center text-[15px] font-bold text-white shadow-landing-red transition-all hover:-translate-y-0.5 hover:bg-landing-red-hover"
          >
            Ir al curso
          </Link>
        ) : (
          <button
            type="button"
            onClick={confirmEnroll}
            disabled={isConfirming}
            className="w-full rounded-[8px] bg-landing-red px-6 py-3 text-[15px] font-bold text-white shadow-landing-red transition-all hover:-translate-y-0.5 hover:bg-landing-red-hover disabled:translate-y-0 disabled:opacity-50"
          >
            {isConfirming ? "Inscribiendo…" : "Sí, inscribirme"}
          </button>
        )}

        <div className="mt-5 text-center">
          <Link to={`/courses/${slug}`} className="text-[14px] text-landing-muted underline">
            Ver el curso primero
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 text-center">
        <p className="text-[13px] font-medium uppercase tracking-wide text-landing-red">
          {grantedLevel === COURSE_ACCESS_LEVEL.FULL ? "Inscripción gratuita" : "Muestra gratuita"}
        </p>
        <h1 className="mt-2 font-display text-[22px] font-bold leading-tight tracking-tight text-landing-text">
          {course.title}
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-landing-muted">
          {step === "form"
            ? "Completá tus datos para acceder al curso."
            : "Ingresá el código que te dimos para confirmar."}
        </p>
      </div>

      {step === "form" && (
        <form onSubmit={handleSubmit(handleSendOTP)} className="grid gap-4">
          <div className="grid gap-2">
            <label htmlFor="firstName" className="text-[13px] font-medium text-landing-text">
              {t("registerView.field.firstName")}
            </label>
            <input
              id="firstName"
              type="text"
              placeholder="Juan"
              className={cn(inputClassName, errors.firstName && "border-landing-red")}
              {...register("firstName")}
            />
            {errors.firstName?.message && (
              <p className="text-[13px] text-landing-red">{errors.firstName.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <label htmlFor="lastName" className="text-[13px] font-medium text-landing-text">
              {t("registerView.field.lastName")}
            </label>
            <input
              id="lastName"
              type="text"
              placeholder="Perez"
              className={cn(inputClassName, errors.lastName && "border-landing-red")}
              {...register("lastName")}
            />
            {errors.lastName?.message && (
              <p className="text-[13px] text-landing-red">{errors.lastName.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <label htmlFor="phone" className="text-[13px] font-medium text-landing-text">
              {t("registerView.field.phone")}
            </label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneField
                  id="phone"
                  value={field.value}
                  onChange={(value) => {
                    // Un límite de envíos de ESE número no traba el botón para otro número.
                    if ((value ?? "") !== field.value) phoneChanged();
                    field.onChange(value ?? "");
                  }}
                  invalid={!!errors.phone}
                />
              )}
            />
            {errors.phone?.message && (
              <p className="text-[13px] text-landing-red">{errors.phone.message}</p>
            )}
          </div>

          <TermsCheckbox
            checked={termsAccepted}
            error={termsError && !termsAccepted}
            errorNonce={termsErrorNonce}
            onCheckedChange={(value) => {
              setTermsAccepted(value);
              if (value) setTermsError(false);
            }}
          />

          <BotTrap ref={guard.trapRef} />
          {/* "Reintentar" es el mismo submit: vuelve a validar nombre, apellido y teléfono. */}
          <AuthGuardSlot guard={guard} onRetry={handleSubmit(handleSendOTP)} />

          <button
            type="submit"
            className="w-full rounded-[8px] bg-landing-red px-6 py-3 text-[15px] font-bold text-white shadow-landing-red transition-all hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover disabled:translate-y-0 disabled:opacity-50 disabled:shadow-none"
            disabled={!isValid || isAuthGuardLocked(guard)}
          >
            <AuthGuardButtonLabel guard={guard} idle="Continuar" sending="Enviando…" />
          </button>
        </form>
      )}

      {step === "otp" && (
        <OtpVerificationCard
          phone={getValues("phone")}
          onVerify={handleVerifyAndEnroll}
          onResend={handleSendOTP}
          onChangePhone={() => setStep("form")}
          isVerifying={isEnrolling}
          error={otpError}
        >
          {/* El widget tiene que seguir montado acá: "Reenviar código" vuelve a llamar a
              handleSendOTP, que pasa por el chequeo anti-bot. Sin esto no hay quien emita
              un token en este paso y el reenvío falla siempre — invisible en staging, que
              tiene Turnstile apagado, y roto en producción, que lo tiene prendido.
              "Reintentar" reenvía por la tarjeta: es la que lleva la espera de 60 s (acá la
              página no tiene una propia, y sin ella el reenvío rebotaba con
              OTP_RESEND_TOO_SOON). */}
          {({ resend }) => (
            <AuthGuardSlot
              guard={guard}
              onRetry={resend}
              noticeClassName="text-left"
              showProgress
            />
          )}
        </OtpVerificationCard>
      )}

      <div className="mt-5 text-center text-[14px] text-landing-muted">
        ¿Ya tenés cuenta?{" "}
        <Link to="/auth/login" className="font-medium text-landing-red underline">
          Iniciá sesión
        </Link>
      </div>
    </Shell>
  );
}
